import { letters } from "./content/letters";
import {
  consonantByGlyph,
  matraBySign,
  numeralByGlyph,
  signByGlyph,
  vowelByGlyph,
} from "./content/registry";

/**
 * A single decomposed unit of a Devanagari string — either an akshara
 * (consonant cluster + optional matra/signs), an independent vowel, a
 * numeral, or an ungradeable "other" unit (space, punctuation, danda,
 * Latin text, etc.).
 */
export interface Akshara {
  /** The source slice for this unit. */
  text: string;
  /** Consonant/nukta letter ids, in order (halant clusters). */
  consonantIds: string[];
  /** Matra id, or null (inherent 'a' or vowel/other). */
  matraId: string | null;
  /** Independent vowel id when the unit IS a vowel. */
  vowelId: string | null;
  /** Anusvara/chandrabindu/visarga attached to this unit. */
  signIds: string[];
  /** Devanagari numeral id, or null. */
  numeralId: string | null;
  /** True for punctuation/space/danda/latin — not gradeable. */
  other: boolean;
}

const HALANT = "्";
const NUKTA = "़";

/** Trailing sign glyphs (anusvara, chandrabindu, visarga) that attach to a unit. */
const TRAILING_SIGN_GLYPHS = new Set(["ं", "ँ", "ः"]);

/** Base consonant glyph -> nukta letter id, built from the registry's nukta letters. */
const nuktaIdByBaseGlyph: ReadonlyMap<string, string> = new Map(
  letters
    .filter((letter) => letter.kind === "nukta")
    .map((letter) => {
      // Nukta glyphs are stored NFC-decomposed: base consonant + U+093C.
      const base = letter.glyph.slice(0, letter.glyph.length - 1);
      return [base, letter.id];
    }),
);

/**
 * Decompose an NFC-normalized Devanagari string into aksharas — the basic
 * orthographic units used for word-building tiles and letter highlighting.
 *
 * The concatenation of every returned unit's `text` reconstructs the
 * NFC-normalized input exactly.
 */
export function parseAksharas(input: string): Akshara[] {
  const normalized = input.normalize("NFC");
  const chars = Array.from(normalized);
  const result: Akshara[] = [];
  let i = 0;

  while (i < chars.length) {
    const char = chars[i] as string;
    const consonantLetter = consonantByGlyph.get(char);

    if (consonantLetter !== undefined) {
      const unit = parseConsonantUnit(chars, i);
      result.push(unit.akshara);
      i = unit.next;
      continue;
    }

    const vowelLetter = vowelByGlyph.get(char);
    if (vowelLetter !== undefined) {
      const unit = parseVowelUnit(chars, i, vowelLetter.id);
      result.push(unit.akshara);
      i = unit.next;
      continue;
    }

    const numeralLetter = numeralByGlyph.get(char);
    if (numeralLetter !== undefined) {
      result.push({
        text: char,
        consonantIds: [],
        matraId: null,
        vowelId: null,
        signIds: [],
        numeralId: numeralLetter.id,
        other: false,
      });
      i += 1;
      continue;
    }

    // Anything else (space, danda, comma, Latin, stray combining marks) is
    // not gradeable on its own.
    result.push({
      text: char,
      consonantIds: [],
      matraId: null,
      vowelId: null,
      signIds: [],
      numeralId: null,
      other: true,
    });
    i += 1;
  }

  return result;
}

/** Resolve the consonant/nukta id at `chars[i]`, and how many chars it consumed (1 or 2). */
function resolveConsonantAt(
  chars: string[],
  i: number,
): { id: string; length: number } | null {
  const char = chars[i];
  if (char === undefined) {
    return null;
  }
  const consonantLetter = consonantByGlyph.get(char);
  if (consonantLetter === undefined) {
    return null;
  }
  // Look ahead for a nukta sign forming a nukta letter (e.g. ढ + ़ -> ढ़).
  if (chars[i + 1] === NUKTA) {
    const nuktaId = nuktaIdByBaseGlyph.get(char);
    if (nuktaId !== undefined) {
      return { id: nuktaId, length: 2 };
    }
  }
  return { id: consonantLetter.id, length: 1 };
}

/**
 * Parse a consonant cluster starting at index `start`: the opening consonant,
 * any halant-joined consonants that follow, then an optional matra and
 * trailing signs. Returns the built akshara and the index just past it.
 */
function parseConsonantUnit(
  chars: string[],
  start: number,
): { akshara: Akshara; next: number } {
  const consonantIds: string[] = [];
  let i = start;

  const first = resolveConsonantAt(chars, i);
  if (first === null) {
    throw new Error(`Expected a consonant at index ${i}`);
  }
  consonantIds.push(first.id);
  i += first.length;

  let halantSignIds: string[] = [];

  // Extend the cluster: halant + consonant repeatedly.
  while (chars[i] === HALANT) {
    const next = resolveConsonantAt(chars, i + 1);
    if (next === null) {
      // Word-final (or otherwise unresolved) halant: keep it as part of the
      // text, half-formed, and record sign-halant.
      halantSignIds = ["sign-halant"];
      i += 1;
      break;
    }
    consonantIds.push(next.id);
    i += 1 + next.length;
  }

  let matraId: string | null = null;
  if (halantSignIds.length === 0) {
    const matraLetter = matraBySign.get(chars[i] ?? "");
    if (matraLetter !== undefined) {
      matraId = matraLetter.id;
      i += 1;
    }
  }

  const trailing = collectTrailingSigns(chars, i);
  const signIds = [...halantSignIds, ...trailing.signIds];
  i = trailing.next;

  return {
    akshara: {
      text: chars.slice(start, i).join(""),
      consonantIds,
      matraId,
      vowelId: null,
      signIds,
      numeralId: null,
      other: false,
    },
    next: i,
  };
}

/** Parse an independent vowel unit, including any trailing signs. */
function parseVowelUnit(
  chars: string[],
  start: number,
  vowelId: string,
): { akshara: Akshara; next: number } {
  const trailing = collectTrailingSigns(chars, start + 1);

  return {
    akshara: {
      text: chars.slice(start, trailing.next).join(""),
      consonantIds: [],
      matraId: null,
      vowelId,
      signIds: trailing.signIds,
      numeralId: null,
      other: false,
    },
    next: trailing.next,
  };
}

/**
 * Collect zero or more trailing sign glyphs (anusvara, chandrabindu,
 * visarga) starting at index `start`. Returns the collected sign ids and the
 * index just past the last one consumed.
 */
function collectTrailingSigns(
  chars: string[],
  start: number,
): { signIds: string[]; next: number } {
  const signIds: string[] = [];
  let i = start;
  while (true) {
    const char = chars[i];
    if (char === undefined || !TRAILING_SIGN_GLYPHS.has(char)) {
      break;
    }
    const signLetter = signByGlyph.get(char);
    if (signLetter === undefined) {
      break;
    }
    signIds.push(signLetter.id);
    i += 1;
  }
  return { signIds, next: i };
}
